import React from 'react';
import { motion } from 'framer-motion';
import { Activity, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AnalysisPanel = ({ analysisData, recommendedStyles = [], avoidStyles = [] }) => {
  const { t } = useTranslation('admin');

  if (!analysisData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-12 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
          <div className="text-center text-zinc-400">
            <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium mb-1">{t('admin.no_analysis_available')}</p>
            <p className="text-xs">{t('admin.upload_for_analysis')}</p>
          </div>
        </div>
      </div>
    );
  }

  const { faceShape, metrics, hairProfile, haircutStructure } = analysisData;
  const mergedRecommendedStyles = Array.isArray(analysisData.recommendedStyles)
    ? analysisData.recommendedStyles
    : recommendedStyles;
  const mergedAvoidStyles = Array.isArray(analysisData.avoidStyles)
    ? analysisData.avoidStyles
    : avoidStyles;
  const hasRecommendations = mergedRecommendedStyles.length > 0 || mergedAvoidStyles.length > 0;
  const hasHairProfile = hairProfile && typeof hairProfile === 'object';

  const prettyValue = (value) => {
    if (typeof value !== 'string' || !value.trim()) return 'Unknown';
    if (value === 'unknown') return 'Unknown';
    return value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-4">
      {faceShape && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-[#7d6633]/10 flex items-center justify-center">
              <svg className="h-4 w-4 text-[#7d6633]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('admin.section_face_shape')}</h4>
            </div>
          </div>
          <div className="pl-10">
            <p className="text-2xl font-bold text-[#7d6633] dark:text-[#a0824a] mb-1 capitalize">
              {faceShape}
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {t('admin.face_shape_subtitle')}
            </p>
          </div>
        </motion.div>
      )}

      {metrics && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10 overflow-hidden"
        >
          <div className="p-3 bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-white/10">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-[#7d6633]" />
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('admin.section_facial_measurements')}</h4>
            </div>
          </div>
          <div className="divide-y divide-zinc-200 dark:divide-white/10">
            {metrics.foreheadWidth !== null && metrics.foreheadWidth !== undefined && (
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.measurement_forehead')}</span>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                  {typeof metrics.foreheadWidth === 'number' ? metrics.foreheadWidth.toFixed(2) : metrics.foreheadWidth}
                </span>
              </div>
            )}
            {metrics.cheekboneWidth !== null && metrics.cheekboneWidth !== undefined && (
              <div className="px-4 py-2 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-800/20">
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.measurement_cheekbone')}</span>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                  {typeof metrics.cheekboneWidth === 'number' ? metrics.cheekboneWidth.toFixed(2) : metrics.cheekboneWidth}
                </span>
              </div>
            )}
            {metrics.jawWidth !== null && metrics.jawWidth !== undefined && (
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.measurement_jaw_width')}</span>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                  {typeof metrics.jawWidth === 'number' ? metrics.jawWidth.toFixed(2) : metrics.jawWidth}
                </span>
              </div>
            )}
            {metrics.jawAngle !== null && metrics.jawAngle !== undefined && (
              <div className="px-4 py-2 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-800/20">
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.measurement_jaw_angle')}</span>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                  {typeof metrics.jawAngle === 'number' ? `${metrics.jawAngle.toFixed(1)}°` : metrics.jawAngle}
                </span>
              </div>
            )}
            {metrics.faceLengthRatio !== null && metrics.faceLengthRatio !== undefined && (
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.measurement_face_ratio')}</span>
                <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                  {typeof metrics.faceLengthRatio === 'number' ? metrics.faceLengthRatio.toFixed(2) : metrics.faceLengthRatio}
                </span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {hasHairProfile && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10 overflow-hidden"
        >
          <div className="p-3 bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-white/10">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#7d6633]" />
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('admin.section_hair_profile')}</h4>
            </div>
          </div>
          <div className="divide-y divide-zinc-200 dark:divide-white/10">
            <div className="px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_curl_type')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.curlType)}</span>
            </div>
            <div className="px-4 py-2 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-800/20">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_density')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.density)}</span>
            </div>
            <div className="px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_strand_thickness')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.strandThickness)}</span>
            </div>
            <div className="px-4 py-2 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-800/20">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_hairline')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.hairline)}</span>
            </div>
            <div className="px-4 py-2 flex items-center justify-between">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_current_length')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.currentLength)}</span>
            </div>
            <div className="px-4 py-2 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-800/20">
              <span className="text-xs text-zinc-700 dark:text-zinc-300">{t('admin.hair_visibility')}</span>
              <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{prettyValue(hairProfile.visibility)}</span>
            </div>
          </div>
        </motion.div>
      )}

      {hasRecommendations && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10 overflow-hidden"
        >
          <div className="p-3 bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-white/10">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#7d6633]" />
              <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('admin.section_recommended_styles')}</h4>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {mergedRecommendedStyles.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 uppercase tracking-wide mb-2">
                  {t('admin.recommended_styles')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {mergedRecommendedStyles.map((style) => (
                    <span
                      key={`recommended-${style}`}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#7d6633]/10 text-[#6a542a] dark:text-[#d9c18d] border border-[#7d6633]/20"
                    >
                      {style}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {mergedAvoidStyles.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 uppercase tracking-wide mb-2">
                  {t('admin.styles_to_avoid')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {mergedAvoidStyles.map((style) => (
                    <span
                      key={`avoid-${style}`}
                      className="px-2.5 py-1 rounded-full text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700"
                    >
                      {style}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {haircutStructure && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-xl bg-white dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10 overflow-hidden"
        >
          <div className="p-3 bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-white/10">
            <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('admin.section_haircut_structure')}</h4>
          </div>
          <div className="p-4">
            <p className="text-sm text-zinc-700 dark:text-zinc-300">
              {`${prettyValue(haircutStructure.topStyle)} + ${prettyValue(haircutStructure.fadeHeight)} Fade + ${prettyValue(haircutStructure.fadeSkin)} + ${prettyValue(haircutStructure.gradient)} Gradient`}
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AnalysisPanel;
